// =============================================================================
// FORM BUILDER - Native form builder component (No external dependencies)
// =============================================================================

import { useCallback } from 'react';
import { Eye, EyeOff, Save, Settings } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { FieldPalette } from './components/FieldPalette';
import { FormCanvas } from './components/FormCanvas';
import { FieldConfigPanel } from './components/FieldConfigPanel';
import { useFormBuilderStore } from './store';
import type { FieldType, Form } from './types';

interface FormBuilderProps {
  onSave?: (form: Form) => void;
}

export function FormBuilder({ onSave }: FormBuilderProps) {
  const {
    sections,
    selectedFieldId,
    previewMode,
    addField,
    togglePreview,
    exportForm,
  } = useFormBuilderStore();

  const handleDragStart = useCallback(
    (event: React.DragEvent, fieldType: FieldType) => {
      event.dataTransfer.setData('application/formfield', fieldType);
      event.dataTransfer.effectAllowed = 'move';
    },
    []
  );

  const handleAddField = useCallback(
    (fieldType: FieldType) => {
      const sectionId = sections[0]?.id;
      if (sectionId) {
        addField(sectionId, fieldType);
      }
    },
    [sections, addField]
  );

  const handleSave = useCallback(() => {
    const form = exportForm();
    if (onSave) {
      onSave(form);
    }
  }, [exportForm, onSave]);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-secondary-900 dark:text-white">
            Form Builder
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={togglePreview}
          >
            {previewMode ? (
              <>
                <EyeOff className="h-4 w-4 mr-2" />
                Éditer
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-2" />
                Aperçu
              </>
            )}
          </Button>
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            Paramètres
          </Button>
          <Button size="sm" onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar - Field palette */}
        {!previewMode && (
          <div className="w-64 border-r border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900">
            <FieldPalette
              onDragStart={handleDragStart}
              onAddField={handleAddField}
            />
          </div>
        )}

        {/* Main canvas */}
        <FormCanvas />

        {/* Right sidebar - Field config panel */}
        {!previewMode && selectedFieldId && (
          <div className="w-80 border-l border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900">
            <FieldConfigPanel />
          </div>
        )}
      </div>
    </div>
  );
}
